/**
 * MFA Enrollment Page
 *
 * Full-page enrollment flow for users who haven't yet set up MFA.
 * Required for all users before accessing the main app (HIPAA compliance).
 *
 * Flow: Enter phone number → Send SMS code → Verify code → Enrolled
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import {
  RecaptchaVerifier,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  multiFactor,
} from 'firebase/auth';
import { auth } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import {
  Smartphone,
  Shield,
  Check,
  AlertCircle,
  Loader2,
  ArrowRight,
} from 'lucide-react';

type EnrollmentStep = 'phone' | 'verify' | 'success';

export function MFAEnrollment(): JSX.Element {
  const { user, signOut } = useAuth();

  const [step, setStep] = useState<EnrollmentStep>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);
  const recaptchaContainerRef = useRef<HTMLDivElement>(null);

  // Initialize invisible recaptcha
  const initRecaptcha = useCallback(() => {
    if (recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current.clear();
      recaptchaVerifierRef.current = null;
    }

    if (recaptchaContainerRef.current) {
      recaptchaVerifierRef.current = new RecaptchaVerifier(auth, recaptchaContainerRef.current, {
        size: 'invisible',
      });
    }
  }, []);

  useEffect(() => {
    initRecaptcha();
    return () => {
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      }
    };
  }, [initRecaptcha]);

  // Format phone number for display as user types
  const formatPhoneDisplay = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '');
    if (digits.length <= 10) {
      setPhoneNumber(digits);
    }
  };

  // Map Firebase error codes to user-friendly messages
  const getErrorMessage = (err: any): string => {
    const code = err?.code || '';
    switch (code) {
      case 'auth/invalid-phone-number':
        return 'Please enter a valid US phone number.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please wait a few minutes and try again.';
      case 'auth/invalid-verification-code':
        return "That code didn't match. Please try again.";
      case 'auth/code-expired':
        return 'The verification code has expired. Please request a new one.';
      case 'auth/requires-recent-login':
        return 'Your session has expired. Please sign out and sign back in.';
      case 'auth/unverified-email':
        return 'Please verify your email before enrolling in MFA.';
      default:
        return 'Something went wrong. Please try again.';
    }
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || phoneNumber.length !== 10) return;

    setError(null);
    setIsLoading(true);

    try {
      // Reinitialize recaptcha if needed
      if (!recaptchaVerifierRef.current) {
        initRecaptcha();
      }

      const multiFactorSession = await multiFactor(user).getSession();

      const phoneInfoOptions = {
        phoneNumber: `+1${phoneNumber}`,
        session: multiFactorSession,
      };

      const phoneAuthProvider = new PhoneAuthProvider(auth);
      const verId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions,
        recaptchaVerifierRef.current!
      );

      setVerificationId(verId);
      setStep('verify');
    } catch (err: any) {
      console.error('Error sending MFA verification code:', err);
      setError(getErrorMessage(err));
      // Re-init recaptcha after error
      initRecaptcha();
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !verificationId || verificationCode.length !== 6) return;

    setError(null);
    setIsLoading(true);

    try {
      const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(credential);

      await multiFactor(user).enroll(multiFactorAssertion, 'Phone');
      setStep('success');
    } catch (err: any) {
      console.error('Error verifying MFA code:', err);
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinueToApp = () => {
    // Force a re-render by reloading — the auth state listener will pick up the enrolled user
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-airy">
      <div className="max-w-md w-full space-y-6 p-8 bg-white rounded-lg shadow-card border border-soft-neutral">
        {/* Header */}
        <div className="text-center">
          <div className="w-16 h-16 bg-evergreen/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-evergreen" />
          </div>
          <h1 className="text-2xl font-bold text-ink">Set Up Two-Factor Authentication</h1>
          <p className="text-muted text-sm mt-2">
            To protect sensitive health information, Hesvara requires two-factor authentication for all accounts.
          </p>
        </div>

        {/* Step: Enter Phone Number */}
        {step === 'phone' && (
          <form onSubmit={handleSendCode} className="space-y-4">
            <div>
              <label className="label">Phone Number</label>
              <div className="flex gap-2">
                <div className="flex items-center px-3 bg-airy border border-soft-neutral rounded-lg text-sm text-muted">
                  +1
                </div>
                <input
                  type="tel"
                  value={formatPhoneDisplay(phoneNumber)}
                  onChange={handlePhoneChange}
                  placeholder="(555) 123-4567"
                  className="input flex-1"
                  required
                  disabled={isLoading}
                  autoFocus
                />
              </div>
              <p className="helper-text mt-1.5">
                We'll send a verification code to this number each time you sign in.
              </p>
            </div>

            {error && (
              <div className="alert alert-error flex items-center gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                {error}
              </div>
            )}

            <button
              type="submit"
              className="btn-primary w-full flex items-center justify-center gap-2"
              disabled={isLoading || phoneNumber.length !== 10}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending Code...
                </>
              ) : (
                <>
                  <Smartphone className="w-4 h-4" />
                  Send Verification Code
                </>
              )}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={signOut}
                className="text-sm text-muted hover:text-slate-ink hover:underline"
              >
                Sign out
              </button>
            </div>
          </form>
        )}

        {/* Step: Verify Code */}
        {step === 'verify' && (
          <form onSubmit={handleVerifyCode} className="space-y-4">
            <div className="text-center mb-2">
              <p className="text-sm text-muted">
                Enter the 6-digit code sent to{' '}
                <span className="font-semibold text-ink">
                  +1 {formatPhoneDisplay(phoneNumber)}
                </span>
              </p>
            </div>

            <div>
              <label className="label">Verification Code</label>
              <input
                type="text"
                inputMode="numeric"
                value={verificationCode}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '');
                  if (val.length <= 6) setVerificationCode(val);
                }}
                placeholder="000000"
                className="input text-center text-2xl tracking-[0.5em] font-mono"
                maxLength={6}
                required
                disabled={isLoading}
                autoFocus
              />
            </div>

            {error && (
              <div className="alert alert-error flex items-center gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                {error}
              </div>
            )}

            <button
              type="submit"
              className="btn-primary w-full flex items-center justify-center gap-2"
              disabled={isLoading || verificationCode.length !== 6}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify & Enable 2FA'
              )}
            </button>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => {
                  setStep('phone');
                  setVerificationCode('');
                  setError(null);
                }}
                className="text-sm text-muted hover:text-slate-ink hover:underline"
              >
                Use a different number
              </button>
              <button
                type="button"
                onClick={() => {
                  setVerificationCode('');
                  setError(null);
                  handleSendCode(new Event('submit') as any);
                }}
                className="text-sm text-evergreen hover:underline"
                disabled={isLoading}
              >
                Resend code
              </button>
            </div>
          </form>
        )}

        {/* Step: Success */}
        {step === 'success' && (
          <div className="space-y-4">
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-evergreen/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-evergreen" />
              </div>
              <p className="text-lg font-semibold text-ink">Two-Factor Authentication Enabled</p>
              <p className="text-sm text-muted mt-1">
                Your account is now protected with SMS verification.
              </p>
            </div>

            <button
              onClick={handleContinueToApp}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              Continue to App
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Invisible reCAPTCHA container */}
      <div ref={recaptchaContainerRef} id="recaptcha-container-enroll" />
    </div>
  );
}

export default MFAEnrollment;
